import type {
  RunnerDispatchPolicyPromptPreset,
  RunnerDispatchPolicy,
  RunnerDispatchPolicyRecommendation,
} from '@agentteams/runner-request/ui/lib/api';
export type {
  RunnerDispatchPolicyPromptPreset,
  RunnerDispatchPolicy,
  RunnerDispatchPolicyRecommendation,
} from '@agentteams/runner-request/ui/lib/api';
import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { RunnerDispatchPolicyEntityType } from '@agentteams/core-constants';

export type { RunnerDispatchPolicyEntityType } from '@agentteams/core-constants';

export type CreateRunnerDispatchPolicyInput = {
  entityType: RunnerDispatchPolicyEntityType;
  entityState: string;
  promptPresetId: string;
};

export type UpdateRunnerDispatchPolicyInput = Partial<CreateRunnerDispatchPolicyInput>;

export type CreateRunnerDispatchPolicyPresetInput = {
  name: string;
  prompt: object;
  userConventionIds?: string[];
  planMode?: boolean;
};

export type UpdateRunnerDispatchPolicyPresetInput = Partial<CreateRunnerDispatchPolicyPresetInput>;

const apiUrl = getApiUrl();

const request = async <T>(token: string, path: string, init?: RequestInit): Promise<T> => {
  const response = await fetchWithAuthRetry(`${apiUrl}${path}`, {
    cache: 'no-store',
    ...init,
    headers: authHeaders(token, Boolean(init?.body)),
  });
  if (!response.ok) throw await parseApiError(response, `Runner dispatch policy request failed: ${response.status}`);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
};

export type RunnerDispatchPoliciesWithRecommendation = {
  policies: RunnerDispatchPolicy[];
  recommendation: RunnerDispatchPolicyRecommendation;
};

const fetchRunnerDispatchPoliciesEnvelope = async (
  token: string,
  projectId: string,
  params?: { entityType?: RunnerDispatchPolicyEntityType; entityState?: string; entityId?: string },
): Promise<{ data: RunnerDispatchPolicy[]; recommendation?: RunnerDispatchPolicyRecommendation }> => {
  const search = new URLSearchParams();
  if (params?.entityType) search.set('entityType', params.entityType);
  if (params?.entityState) search.set('entityState', params.entityState);
  if (params?.entityId) search.set('entityId', params.entityId);
  const suffix = search.toString();
  return request<{ data: RunnerDispatchPolicy[]; recommendation?: RunnerDispatchPolicyRecommendation }>(
    token,
    `/api/projects/${projectId}/runner-dispatch-policies${suffix ? `?${suffix}` : ''}`,
  );
};

export const fetchRunnerDispatchPolicies = async (
  token: string,
  projectId: string,
  params?: { entityType?: RunnerDispatchPolicyEntityType; entityState?: string },
): Promise<RunnerDispatchPolicy[]> => {
  const result = await fetchRunnerDispatchPoliciesEnvelope(token, projectId, params);
  return result.data;
};

/** 러너 요청 컨텍스트용 조회: entityId를 함께 보내 서버 추천(코드리뷰 OPEN)을 받는다. */
export const fetchRunnerDispatchPoliciesWithRecommendation = async (
  token: string,
  projectId: string,
  params: { entityType: RunnerDispatchPolicyEntityType; entityState: string; entityId?: string },
): Promise<RunnerDispatchPoliciesWithRecommendation> => {
  const result = await fetchRunnerDispatchPoliciesEnvelope(token, projectId, params);
  return { policies: result.data, recommendation: result.recommendation ?? null };
};

export const createRunnerDispatchPolicy = async (
  token: string,
  projectId: string,
  input: CreateRunnerDispatchPolicyInput,
): Promise<RunnerDispatchPolicy> => {
  const result = await request<{ data: RunnerDispatchPolicy }>(
    token,
    `/api/projects/${projectId}/runner-dispatch-policies`,
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );
  return result.data;
};

export const updateRunnerDispatchPolicy = async (
  token: string,
  projectId: string,
  id: string,
  input: UpdateRunnerDispatchPolicyInput,
): Promise<RunnerDispatchPolicy> => {
  const result = await request<{ data: RunnerDispatchPolicy }>(
    token,
    `/api/projects/${projectId}/runner-dispatch-policies/${id}`,
    { method: 'PUT', body: JSON.stringify(input) },
  );
  return result.data;
};

export const reorderRunnerDispatchPolicies = async (
  token: string,
  projectId: string,
  ids: string[],
): Promise<RunnerDispatchPolicy[]> => {
  const result = await request<{ data: RunnerDispatchPolicy[] }>(
    token,
    `/api/projects/${projectId}/runner-dispatch-policies/order`,
    { method: 'PUT', body: JSON.stringify({ ids }) },
  );
  return result.data;
};

export const deleteRunnerDispatchPolicy = async (token: string, projectId: string, id: string): Promise<void> =>
  request<void>(token, `/api/projects/${projectId}/runner-dispatch-policies/${id}`, { method: 'DELETE' });

export const createRunnerDispatchPolicyPreset = async (
  token: string,
  projectId: string,
  input: CreateRunnerDispatchPolicyPresetInput,
): Promise<RunnerDispatchPolicyPromptPreset> => {
  const result = await request<{ data: RunnerDispatchPolicyPromptPreset }>(
    token,
    `/api/projects/${projectId}/runner-dispatch-policies/prompt-presets`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return result.data;
};

export const updateRunnerDispatchPolicyPreset = async (
  token: string,
  projectId: string,
  id: string,
  input: UpdateRunnerDispatchPolicyPresetInput,
): Promise<RunnerDispatchPolicyPromptPreset> => {
  const result = await request<{ data: RunnerDispatchPolicyPromptPreset }>(
    token,
    `/api/projects/${projectId}/runner-dispatch-policies/prompt-presets/${id}`,
    { method: 'PUT', body: JSON.stringify(input) },
  );
  return result.data;
};

export const deleteRunnerDispatchPolicyPreset = async (token: string, projectId: string, id: string): Promise<void> =>
  request<void>(token, `/api/projects/${projectId}/runner-dispatch-policies/prompt-presets/${id}`, {
    method: 'DELETE',
  });
